import useSWR from "swr";
/*
 * Retaining historical comment rows changes pagination: a server page can contain 50 open rows
 * but zero cards anchored in the current manuscript. Reuse the store's annotation projection
 * and the sidebar's status filter so fetching measures the same visible cards the user sees.
 */
import { getRelevantComments, useCommentStore } from "../stores/commentStore";
import CommentModel from "../models/Comment";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import { getApiResponseEndpoint, getApiResponseStatus, isExpectedApiResponseError } from "../Auth";
import { filterCommentsByType } from "../components/TiptapDocumentSB/CommentSidebar/commentStatus";

const COMMENTS_PAGE_SIZE = 50;
const MAX_PAGES_PER_LOAD = 5;

function getAnnotationFilter(documentId) {
  const state = useCommentStore.getState();
  return state.documentId === documentId && state.documentAnnotationsReady
    ? { annotationIds: state.documentAnnotationIds || [] }
    : {};
}

// Module-level ref to track which document should be showing comments
// This prevents race conditions where a fetch for doc A completes while viewing doc B
const activeDocumentRef = { current: null };

function isPaginatedCommentsResponse(data) {
  return data && typeof data === "object" && !Array.isArray(data) && Array.isArray(data.comments);
}

function getCommentsFromPayload(data) {
  if (Array.isArray(data)) return data;
  if (isPaginatedCommentsResponse(data)) return data.comments;
  return [];
}

function getPaginationFromPayload(data) {
  if (!isPaginatedCommentsResponse(data)) {
    return {
      hasMore: false,
      nextCursor: null,
      counts: null,
      /*
       * The metadata parser supports both legacy array responses and paginated objects. Missing IDs
       * remain null (unknown), while [] is an authoritative empty server set; neither is the same
       * thing as the current document's annotation list.
       */
      openAnnotationIds: null,
    };
  }
  return {
    hasMore: data.hasMore === true,
    nextCursor: data.nextCursor || null,
    counts: data.counts || null,
    /*
     * Carry the additive server ID list through page normalization without inventing IDs from the
     * loaded rows. This preserves exact counts even when the matching comment bodies are on later
     * pages.
     */
    openAnnotationIds: Array.isArray(data.openAnnotationIds) ? data.openAnnotationIds : null,
  };
}

function getCountsFromPayload(data) {
  if (data && typeof data === "object" && data.counts && typeof data.counts === "object") {
    return data.counts;
  }
  return null;
}

/*
 * Counts-only responses and page responses share this optional metadata. The next helper counts
 * only the requested status, then applies annotation visibility for the active document's Open
 * filter. Resolved/ignored lists must not suddenly require live manuscript marks.
 */
function getOpenAnnotationIdsFromPayload(data) {
  return Array.isArray(data?.openAnnotationIds) ? data.openAnnotationIds : null;
}

function getVisibleCommentCount(comments, documentId, filterType) {
  const state = useCommentStore.getState();
  const filteredComments = filterCommentsByType(comments, filterType);
  if (filterType !== "open" || state.documentId !== documentId) return filteredComments.length;
  return getRelevantComments(filteredComments, state.documentAnnotationIds, state.documentAnnotationsReady).length;
}

async function fetchPaginatedComments(docId, filterType, annotationFilter) {
  const commentState = useCommentStore.getState();
  const loadedCount = commentState.documentId === docId && Array.isArray(commentState.allComments)
    ? getVisibleCommentCount(commentState.allComments, docId, filterType)
    : 0;
  const minimumCommentCount = Math.max(COMMENTS_PAGE_SIZE, loadedCount);

  const [pageResult, countsResult] = await Promise.allSettled([
    CommentModel.getAll(docId, { includeResolved: true, limit: COMMENTS_PAGE_SIZE, type: filterType, ...annotationFilter }),
    CommentModel.getCounts(docId, annotationFilter),
  ]);

  if (pageResult.status === "rejected") {
    throw pageResult.reason;
  }

  let comments = getCommentsFromPayload(pageResult.value);
  let page = getPaginationFromPayload(pageResult.value);
  /*
   * Revalidate toward the number of visible cards previously loaded, not retained hidden rows.
   * Current servers filter Open pages by manuscript IDs; older servers get a bounded fallback.
   * Cap a request at five pages and retain its cursor so manual pagination can continue.
   */
  const requestedCursors = new Set();

  while (page.hasMore && page.nextCursor && comments.length < minimumCommentCount && requestedCursors.size < MAX_PAGES_PER_LOAD - 1) {
    if (requestedCursors.has(page.nextCursor)) {
      throw new Error("Comment pagination cursor did not advance");
    }
    requestedCursors.add(page.nextCursor);
    const nextPage = await CommentModel.getAll(docId, {
      includeResolved: true,
      limit: COMMENTS_PAGE_SIZE,
      cursor: page.nextCursor,
      type: filterType,
      ...annotationFilter,
    });
    comments = mergeCommentPages(comments, getCommentsFromPayload(nextPage));
    page = getPaginationFromPayload(nextPage);
  }

  const counts = countsResult.status === "fulfilled"
    ? getCountsFromPayload(countsResult.value) || page.counts
    : page.counts;
  /*
   * The counts request supplies matching open IDs independently of body pagination.
   * Once manuscript IDs are authoritative, the server restricts this metadata to those IDs.
   * If that request fails or an older response omits the field, retain whatever metadata the page
   * provided; comment-body loading can still succeed.
   */
  const openAnnotationIds = countsResult.status === "fulfilled"
    ? getOpenAnnotationIdsFromPayload(countsResult.value) || page.openAnnotationIds
    : page.openAnnotationIds;

  return buildCachePayload(comments, pageResult.value, {
    hasMore: page.hasMore,
    nextCursor: page.nextCursor,
    counts,
    openAnnotationIds,
  });
}

function mergeCommentPages(currentComments, nextComments) {
  const merged = [];
  const indexById = new Map();

  [...(currentComments || []), ...(nextComments || [])].forEach((comment) => {
    if (!comment?.id) return;
    const existingIndex = indexById.get(comment.id);
    if (existingIndex === undefined) {
      indexById.set(comment.id, merged.length);
      merged.push(comment);
      return;
    }
    merged[existingIndex] = comment;
  });

  return merged;
}

/*
 * SWR cache mutations must preserve counts and the complete open-ID set alongside rows and
 * cursors. Otherwise an optimistic edit or load-more could accidentally revert exact badge
 * counting to a page-sized approximation. The array-response compatibility path is retained
 * when no pagination metadata exists.
 */
function buildCachePayload(comments, currentPayload, pagination) {
  if (isPaginatedCommentsResponse(currentPayload) || pagination?.counts || Array.isArray(pagination?.openAnnotationIds)) {
    return {
      ...(isPaginatedCommentsResponse(currentPayload) ? currentPayload : {}),
      comments,
      hasMore: pagination?.hasMore === true,
      nextCursor: pagination?.nextCursor || null,
      counts: pagination?.counts || currentPayload?.counts || null,
      openAnnotationIds: pagination?.openAnnotationIds ?? currentPayload?.openAnnotationIds ?? null,
    };
  }
  return comments;
}

function getCommentsLoadRequestIdentity(documentId, userId, isAuthor, filterType) {
  return `${documentId || ""}:${userId || ""}:${isAuthor ? "author" : "reader"}:${filterType || "open"}`;
}

export function useComments({ isAuthor, documentId, userId, filterType = "open" }) {
  const setComments = useCommentStore((state) => state.setComments);
  const setMutateComments = useCommentStore((state) => state.setMutateComments);
  const setUpdateCommentCounts = useCommentStore((state) => state.setUpdateCommentCounts);
  const setCommentsLoaded = useCommentStore((state) => state.setCommentsLoaded);
  const setCommentCounts = useCommentStore((state) => state.setCommentCounts);
  /*
   * Subscribe to both count metadata and document readiness. The visible count is status-filtered
   * because the store intentionally also retains resolved/ignored cards; those must not satisfy
   * the target for a page of Open cards.
   */
  const setOpenCommentAnnotationIds = useCommentStore((state) => state.setOpenCommentAnnotationIds);
  const commentsLoaded = useCommentStore((state) => state.commentsLoaded);
  const documentAnnotationsReady = useCommentStore((state) => state.documentAnnotationsReady);
  const documentAnnotationIds = useCommentStore((state) => state.documentAnnotationIds);
  const documentAnnotationCount = documentAnnotationIds.length;
  const documentAnnotationKey = JSON.stringify([...documentAnnotationIds].sort());
  const lastRequestedAnnotationsRef = useRef(null);
  const visibleCommentCount = useCommentStore((state) => filterCommentsByType(state.comments, filterType).length);
  const setDocumentId = useCommentStore((state) => state.setDocumentId);
  const [pagination, setPagination] = useState({
    hasMore: false,
    nextCursor: null,
    isLoadingMore: false,
    counts: null,
    /*
     * Pagination state now carries the same ID metadata as SWR and the store. It survives
     * same-document paging/refetches instead of being lost whenever a new cursor is written.
     */
    openAnnotationIds: null,
  });
  const paginationRef = useRef(pagination);
  const paginationVersionRef = useRef(0);
  const preservedCommentsForNextLoadRef = useRef(null);
  const loadIdentityRef = useRef(null);
  const activeLoadRequestIdentityRef = useRef(null);
  /*
   * The request ref is a synchronous single-flight lock (React state alone updates too late to
   * stop two calls in one tick). The automatic-request ref remembers a document/version/cursor
   * attempt so failures do not trigger a hot automatic retry loop.
   */
  const loadMoreRequestRef = useRef(null);
  const automaticPageRequestRef = useRef(null);

  useEffect(() => {
    paginationRef.current = pagination;
  }, [pagination]);

  const getPreservedCommentsForLoad = useCallback((docId, nextFilterType) => {
    const preserved = preservedCommentsForNextLoadRef.current;
    if (!preserved || preserved.documentId !== docId || preserved.filterType !== nextFilterType) {
      return [];
    }
    return preserved.comments;
  }, []);

  const preserveCommentsForNextLoad = useCallback((commentsForNextLoad, targetFilterType = filterType) => {
    const comments = Array.isArray(commentsForNextLoad)
      ? commentsForNextLoad.filter(Boolean)
      : [commentsForNextLoad].filter(Boolean);
    if (comments.length === 0 || !documentId) return;
    preservedCommentsForNextLoadRef.current = {
      comments,
      documentId,
      filterType: targetFilterType,
    };
  }, [documentId, filterType]);

  // Invalidate so EditorContainer does not treat stale [] or another doc's threads as "loaded" for this manuscript.
  useLayoutEffect(() => {
    if (!documentId || !userId) return;
    paginationVersionRef.current += 1;
    /*
     * Changing document, user, author mode or filter invalidates the old request generation before
     * effects publish new rows. Clear both manual and automatic request identities; an old network
     * response may finish, but its generation check prevents it from committing.
     */
    loadMoreRequestRef.current = null;
    automaticPageRequestRef.current = null;
    const preservedComments = getPreservedCommentsForLoad(documentId, filterType);
    const nextLoadIdentity = `${documentId}:${userId}:${isAuthor ? "author" : "reader"}`;
    activeLoadRequestIdentityRef.current = getCommentsLoadRequestIdentity(documentId, userId, isAuthor, filterType);
    const loadIdentityChanged = loadIdentityRef.current !== nextLoadIdentity;
    loadIdentityRef.current = nextLoadIdentity;
    const countsForReload = loadIdentityChanged
      ? null
      : paginationRef.current.counts || useCommentStore.getState().commentCounts || null;
    /*
     * Switching document/user/author identity discards old count IDs. Changing only the status
     * filter preserves same-document metadata, because the open-ID list describes the whole
     * document rather than the selected page/filter.
     */
    const openAnnotationIdsForReload = loadIdentityChanged
      ? null
      : paginationRef.current.openAnnotationIds ?? useCommentStore.getState().openCommentAnnotationIds ?? null;
    setCommentsLoaded(false);
    setComments(preservedComments);
    setCommentCounts(countsForReload);
    /*
     * Publish the preserved-or-cleared ID set to the shared store and pagination together. Both
     * toolbar and sidebar read that store, so resetting only one copy could leave them reporting
     * stale counts during navigation.
     */
    setOpenCommentAnnotationIds(openAnnotationIdsForReload);
    setPagination({
      hasMore: false,
      nextCursor: null,
      isLoadingMore: false,
      counts: countsForReload,
      openAnnotationIds: openAnnotationIdsForReload,
    });
    setDocumentId(documentId); // Set the documentId in the store for cross-component sync
    // Update the module-level ref so onSuccess callbacks know which document is active
    activeDocumentRef.current = documentId;
    /*
     * Invalidate the generation on cleanup as well as setup. This covers unmount while a multi-page
     * loop is waiting, so it neither appends old rows nor requests another hidden page after the
     * hook is gone.
     */
    return () => {
      paginationVersionRef.current += 1;
      loadMoreRequestRef.current = null;
    };
  }, [
    documentId,
    userId,
    isAuthor,
    filterType,
    getPreservedCommentsForLoad,
    setCommentCounts,
    /*
     * Include the newly used store setter in the effect dependencies; this is lifecycle wiring for
     * the metadata reset above, not a new trigger to refetch on every annotation change.
     */
    setOpenCommentAnnotationIds,
    setComments,
    setCommentsLoaded,
    setDocumentId,
  ]);

  /*
   * Track background SWR revalidation separately from initial loading. Automatic first-page
   * filling waits for that refetch to finish; manual load-more remains allowed, preserving
   * existing scroll/navigation behavior.
   */
  const { data, error, isLoading, isValidating, mutate } = useSWR(
    documentId && userId ? [documentId, userId, isAuthor, filterType] : null,
    async ([docId, uid, requestIsAuthor, requestFilterType]) => {
      if (!requestIsAuthor) {
        return await CommentModel.getByUser(docId, uid);
      }

      const annotationFilter = getAnnotationFilter(docId);
      lastRequestedAnnotationsRef.current = {
        identity: getCommentsLoadRequestIdentity(docId, uid, requestIsAuthor, requestFilterType),
        ids: annotationFilter.annotationIds ? new Set(annotationFilter.annotationIds) : null,
      };
      return await fetchPaginatedComments(docId, requestFilterType, annotationFilter);
    },
    {
      onError: (err) => {
        console.error("UseCommentsHook Error fetching comments:", err);
        const storeDocId = useCommentStore.getState().documentId;
        const failedLoadIsStillCurrent =
          activeDocumentRef.current === documentId &&
          storeDocId === documentId &&
          activeLoadRequestIdentityRef.current === getCommentsLoadRequestIdentity(documentId, userId, isAuthor, filterType);
        if (failedLoadIsStillCurrent) {
          setPagination((current) => ({
            ...current,
            isLoadingMore: false,
          }));
          setCommentsLoaded(true);
        }
        // Avoid creating a second frontend issue for backend outages/rate
        // limits. Unexpected client-side comment handling errors still report.
        if (isExpectedApiResponseError(err)) {
          return;
        }
        Sentry.captureException(err, {
          tags: {
            documentId,
            userId,
            useCommentsHook: true,
            isAuthor,
            statusCode: getApiResponseStatus(err),
            endpointUrl: getApiResponseEndpoint(err),
          },
        });
      },
      revalidateOnFocus: false,
    }
  );

  useEffect(() => {
    if (!data) return;
    // Only update comments if this document is still the active one. This prevents
    // comments from document A appearing in document B's sidebar when fetches race.
    const storeDocId = useCommentStore.getState().documentId;
    if (activeDocumentRef.current !== documentId || storeDocId !== documentId) return;

    const preservedComments = getPreservedCommentsForLoad(documentId, filterType);
    const comments = mergeCommentPages(preservedComments, getCommentsFromPayload(data));
    const page = getPaginationFromPayload(data);
    const nextCounts =
      page.counts || paginationRef.current.counts || useCommentStore.getState().commentCounts || null;
    /*
     * SWR success must transfer complete ID metadata with the fetched rows and cursor. Use nullish
     * fallback so an authoritative empty list replaces a previous nonempty list instead of being
     * treated as missing.
     */
    const nextOpenAnnotationIds =
      page.openAnnotationIds ?? paginationRef.current.openAnnotationIds ?? useCommentStore.getState().openCommentAnnotationIds ?? null;
    setComments(comments);
    setCommentCounts(nextCounts);
    /*
     * Keep store and local pagination synchronized when SWR supplies new data. Preserved comments
     * remain available for local UI continuity, while the document-index projection decides which
     * open cards currently appear.
     */
    setOpenCommentAnnotationIds(nextOpenAnnotationIds);
    setPagination({
      hasMore: page.hasMore,
      nextCursor: page.nextCursor,
      isLoadingMore: false,
      counts: nextCounts,
      openAnnotationIds: nextOpenAnnotationIds,
    });
    setCommentsLoaded(true);
    if (preservedComments.length > 0) {
      preservedCommentsForNextLoadRef.current = null;
    }
  /*
   * The data-publication effect now closes over the new metadata setter as well. Keeping the
   * dependency list complete avoids stale wiring without changing the SWR request key.
   */
  }, [data, documentId, filterType, getPreservedCommentsForLoad, setCommentCounts, setComments, setCommentsLoaded, setOpenCommentAnnotationIds]);

  const mutateComments = useCallback(
    (...args) => {
      if (args.length === 0) {
        // Feedback streaming also asks for bare refetches. Let next-card/load-more
        // finish before revalidating rather than cancelling navigation mid-flight.
        const pending = loadMoreRequestRef.current;
        return pending ? pending.done.then(() => mutate()) : mutate();
      }
      if (args[0] === undefined && args[1]?.historyRestore) {
        /*
         * Only an explicit History signal invalidates the current view. Cancel the old view's pagination generation
         * immediately, even if documentId and filter stay the same: the manuscript anchors have
         * changed. Reset the loading flag and automatic attempt key so the restored view can fill its
         * own first visible page.
         */
        // A History restore/refetch supersedes pages requested for the old view.
        paginationVersionRef.current += 1;
        loadMoreRequestRef.current = null;
        automaticPageRequestRef.current = null;
        paginationRef.current = { ...paginationRef.current, isLoadingMore: false };
        setPagination(paginationRef.current);
        return mutate();
      }

      const [dataOrFn, options] = args;
      const cacheOptions = options && typeof options === "object" && typeof options.optimisticData === "function"
        ? {
            ...options,
            optimisticData: (payload) => buildCachePayload(
              options.optimisticData(getCommentsFromPayload(payload)), payload, paginationRef.current,
            ),
          }
        : options;

      if (typeof dataOrFn === "function") {
        return mutate(async (currentPayload) => {
          const currentComments = getCommentsFromPayload(currentPayload);
          const result = await dataOrFn(currentComments);
          if (result === undefined) {
            return currentPayload;
          }
          const nextComments = getCommentsFromPayload(result);
          return buildCachePayload(nextComments, currentPayload, paginationRef.current);
        }, cacheOptions).catch(async (mutationError) => {
          // SWR can roll back to the exact same payload reference without firing
          // our data effect. Explicitly republish its rolled-back cache to Zustand;
          // do not restore a captured array over newer cache mutations or another view.
          if (options?.rollbackOnError === true
            && activeLoadRequestIdentityRef.current === getCommentsLoadRequestIdentity(documentId, userId, isAuthor, filterType)) {
            await mutate((payload) => {
              useCommentStore.getState().setCommentRows(getCommentsFromPayload(payload));
              return payload;
            }, false);
          }
          throw mutationError;
        });
      }

      if (Array.isArray(dataOrFn)) {
        return mutate((currentPayload) => buildCachePayload(dataOrFn, currentPayload, paginationRef.current), cacheOptions);
      }

      return mutate(dataOrFn, cacheOptions);
    },
    [mutate, documentId, userId, isAuthor, filterType]
  );

  // A refresh can beat Yjs delivery and ask the server for an outdated annotation
  // set (including []). Later marks must invalidate that filtered result even when
  // it reported hasMore=false. Re-filtering cached rows cannot recover missing rows.
  useEffect(() => {
    if (!isAuthor || filterType !== "open" || !documentAnnotationsReady || isValidating) return;
    const requested = lastRequestedAnnotationsRef.current;
    if (requested?.identity !== getCommentsLoadRequestIdentity(documentId, userId, isAuthor, filterType)
      || !requested.ids || !JSON.parse(documentAnnotationKey).some((id) => !requested.ids.has(id))) return;
    // Coalesce streamed marks, without polling or refetching for ordinary text edits.
    const timer = setTimeout(() => { void mutateComments(); }, 100);
    return () => clearTimeout(timer);
  }, [documentId, userId, isAuthor, filterType, documentAnnotationsReady, documentAnnotationKey, isValidating, mutateComments]);

  const updateCommentCounts = useCallback((updater) => {
    const currentPagination = paginationRef.current;
    const currentCounts = currentPagination.counts || useCommentStore.getState().commentCounts;
    const nextCounts = typeof updater === "function" ? updater(currentCounts) : updater;

    if (!nextCounts || typeof nextCounts !== "object") return;

    const nextPagination = {
      ...currentPagination,
      counts: nextCounts,
    };
    paginationRef.current = nextPagination;
    setPagination(nextPagination);
    setCommentCounts(nextCounts);
    void mutate((currentPayload) => {
      if (!currentPayload) return currentPayload;
      return buildCachePayload(getCommentsFromPayload(currentPayload), currentPayload, nextPagination);
    }, false);
  }, [mutate, setCommentCounts]);

  /*
   * Existing bulk/status operations reset pagination too. They now invalidate any hidden-page
   * loop and clear the automatic attempt key; otherwise a superseded request could append rows
   * after the reset or keep future loading locked out.
   */
  const resetCommentPagination = useCallback((commentsForCache = null, countsUpdater = null) => {
    paginationVersionRef.current += 1;
    loadMoreRequestRef.current = null;
    automaticPageRequestRef.current = null;
    const currentPagination = paginationRef.current;
    const currentCounts = currentPagination.counts || useCommentStore.getState().commentCounts;
    const nextCounts = typeof countsUpdater === "function"
      ? countsUpdater(currentCounts)
      : countsUpdater ?? currentCounts;
    const nextPagination = {
      hasMore: false,
      nextCursor: null,
      isLoadingMore: false,
      counts: nextCounts || null,
      /*
       * Preserve the same document's complete open-ID metadata across a pagination reset. The shared
       * count helper reconciles locally changed statuses until the counts endpoint supplies a fresh
       * set.
       */
      openAnnotationIds: currentPagination.openAnnotationIds ?? useCommentStore.getState().openCommentAnnotationIds ?? null,
    };

    paginationRef.current = nextPagination;
    setPagination(nextPagination);
    setCommentCounts(nextPagination.counts);
    void mutate((currentPayload) => {
      if (!currentPayload && !Array.isArray(commentsForCache)) return currentPayload;
      const nextComments = Array.isArray(commentsForCache)
        ? commentsForCache
        : getCommentsFromPayload(currentPayload);
      return buildCachePayload(nextComments, currentPayload, nextPagination);
    }, false);
  }, [mutate, setCommentCounts]);

  /*
   * One user load-more request now means up to 50 additional visible cards, potentially spanning
   * several raw server pages. Set a synchronous request identity before awaiting anything;
   * require the same identity, document and pagination generation before applying any result.
   * This is logical cancellation, not network abortion.
   */
  const loadMoreComments = useCallback(async ({ fillFirstPage = false } = {}) => {
    const page = paginationRef.current;
    if (!isAuthor || !documentId || !userId || !page.hasMore || page.isLoadingMore || loadMoreRequestRef.current) {
      return { success: false, skipped: true };
    }
    const requestPaginationVersion = paginationVersionRef.current;
    let finishRequest;
    const request = { done: new Promise((resolve) => { finishRequest = resolve; }) };
    loadMoreRequestRef.current = request;
    const isCurrentRequest = () => (
      loadMoreRequestRef.current === request &&
      activeDocumentRef.current === documentId &&
      useCommentStore.getState().documentId === documentId &&
      paginationVersionRef.current === requestPaginationVersion
    );

    paginationRef.current = { ...page, isLoadingMore: true };
    setPagination(paginationRef.current);

    try {
      let mergedComments = useCommentStore.getState().allComments || [];
      let fetchedComments = [];
      let nextPagination = page;
      /*
       * Automatic initial fill targets 50 visible cards total; manual load-more targets the current
       * visible count plus 50. Hidden historical rows are retained in allComments but do not satisfy
       * either target. Stop at the server's end even if fewer than 50 visible cards exist.
       */
      const targetVisibleCount = fillFirstPage
        ? COMMENTS_PAGE_SIZE
        : getVisibleCommentCount(mergedComments, documentId, filterType) + COMMENTS_PAGE_SIZE;
      const requestedCursors = new Set();

      // Hidden History comments still consume server pages. Fetch a visible page
      // per request so neither the scroll sentinel nor next-card navigation stalls.
      /*
       * Every response advances the raw server cursor, even if none of its rows are visible in this
       * History version. Refuse missing/repeated cursors to bound malformed pagination. Re-read the
       * live store when merging each batch so edits to already-loaded cards made during the wait are
       * not overwritten by an old captured array.
       */
      do {
        const cursor = nextPagination.nextCursor;
        if (!cursor || requestedCursors.has(cursor)) {
          throw new Error("Comment pagination cursor did not advance");
        }
        requestedCursors.add(cursor);
        const nextPage = await CommentModel.getAll(documentId, {
          includeResolved: true,
          limit: COMMENTS_PAGE_SIZE,
          cursor,
          type: filterType,
          ...getAnnotationFilter(documentId),
        });
        if (!isCurrentRequest()) return { success: false, stale: true };

        fetchedComments = mergeCommentPages(fetchedComments, getCommentsFromPayload(nextPage));
        // Keep edits to already-loaded cards made while these pages were in flight.
        mergedComments = mergeCommentPages(useCommentStore.getState().allComments || [], fetchedComments);
        nextPagination = getPaginationFromPayload(nextPage);
      } while (
        nextPagination.hasMore &&
        requestedCursors.size < MAX_PAGES_PER_LOAD &&
        getVisibleCommentCount(mergedComments, documentId, filterType) < targetVisibleCount
      );

      /*
       * After reaching the visible target or server end, publish all accumulated rows plus the final
       * cursor. Keep the complete open-ID metadata from the prior counts response when normal page
       * responses do not repeat it. Cache mutation disables refetch because this batch is already
       * fetched.
       */
      const mergedPagination = {
        hasMore: nextPagination.hasMore,
        nextCursor: nextPagination.nextCursor,
        isLoadingMore: false,
        counts: nextPagination.counts || page.counts,
        openAnnotationIds: nextPagination.openAnnotationIds ?? page.openAnnotationIds,
      };

      setComments(mergedComments);
      setCommentCounts(mergedPagination.counts);
      paginationRef.current = mergedPagination;
      setPagination(mergedPagination);
      await mutate(
        (currentPayload) => buildCachePayload(mergedComments, currentPayload, mergedPagination),
        false,
      );
      return { success: true };
    } catch (err) {
      /*
       * An obsolete request's error is not an error for the current view. Suppress stale error
       * publication and, in finally, only unlock the request that still owns the ref; an old
       * completion must not clear a newer request's loading state.
       */
      if (!isCurrentRequest()) return { success: false, stale: true };
      Sentry.captureException(err, {
        tags: {
          documentId,
          userId,
          useCommentsHook: true,
          pagination: true,
        },
      });
      return { success: false, error: err };
    } finally {
      finishRequest();
      if (isCurrentRequest()) {
        loadMoreRequestRef.current = null;
        paginationRef.current = { ...paginationRef.current, isLoadingMore: false };
        setPagination(paginationRef.current);
      }
    }
  }, [documentId, filterType, isAuthor, mutate, setCommentCounts, setComments, userId]);

  /*
   * An empty list cannot scroll to its next-page sentinel. Once Yjs makes annotation visibility
   * authoritative, automatically scan enough pages to fill the first visible page (or exhaust the
   * server, with a five-page cap). Remember the view generation, not the changing cursor, to avoid
   * endless retries. Only the author's Open filter needs this new auto-fill behavior.
   */
  // Comments can load before Yjs makes the annotation index authoritative. Fill
  // the first visible page once synced, without requiring an empty list to scroll.
  useEffect(() => {
    if (!isAuthor || filterType !== "open" || !documentAnnotationsReady || documentAnnotationCount === 0 || !commentsLoaded ||
      isLoading || isValidating || error || pagination.isLoadingMore || !pagination.hasMore ||
      visibleCommentCount >= COMMENTS_PAGE_SIZE) return;
    const requestKey = `${documentId}:${paginationVersionRef.current}`;
    // Do not restart a capped scan just because its last page advanced the cursor.
    if (automaticPageRequestRef.current === requestKey) return;
    automaticPageRequestRef.current = requestKey;
    void loadMoreComments({ fillFirstPage: true });
  }, [commentsLoaded, documentAnnotationsReady, documentAnnotationCount, documentId, error, filterType, isAuthor, isLoading, isValidating, loadMoreComments, pagination.hasMore, pagination.isLoadingMore, pagination.nextCursor, visibleCommentCount]);

  // Store the mutate function in zustand when it's available
  useEffect(() => {
    if (mutateComments) {
      setMutateComments(mutateComments);
    }
  }, [mutateComments, setMutateComments]);

  useEffect(() => {
    setUpdateCommentCounts(updateCommentCounts);
  }, [updateCommentCounts, setUpdateCommentCounts]);

  return {
    error,
    isLoading,
    commentsLoaded,
    hasMoreComments: pagination.hasMore && !(filterType === "open" && documentAnnotationsReady && documentAnnotationCount === 0),
    isLoadingMoreComments: pagination.isLoadingMore,
    loadMoreComments,
    commentCounts: pagination.counts,
    updateCommentCounts,
    resetCommentPagination,
    preserveCommentsForNextLoad,
  };
}
